import MaterialTable, { Column, MaterialTableProps, Query, QueryResult  } from '@material-table/core';
import { ReactElement, useCallback, useRef, useState, useEffect } from 'react';
import { tableIcons } from '../../../ts-components/app-supplier-database/icons';
import styles from './DataTable.module.scss';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PaginationWithGoTo from '../../../ts-components/pagination-with-goto/pagination-with-goto';
import { CircularProgress } from '@mui/material';
import { alpha } from '@material-ui/core/styles';

export type CustomTableProps<T extends object> = MaterialTableProps<T>

type Props<T extends Object> = CustomTableProps<T> & {
  tableRef?: any,
  title?: string | ReactElement,
  isLoading: boolean,
  columns: Column<T>[],
  data: T[] | ((query: Query<object>) => Promise<QueryResult<T>>),
  onRowClick?: (
    event?: React.MouseEvent,
    rowData?: T,
    toggleDetailPanel?: (panelIndex?: number) => void
  ) => void,
  pageSize?: number,
  style?: React.CSSProperties,
  isScrollable?: boolean,
  sorting?: boolean,
  defaultOrderBy?: number,
  defaultOrderDirection?: 'asc' | 'desc',
  defaultOrderByField?: string,
  onOrderChange?: (orderBy: number, orderDirection: 'asc' | 'desc', orderByField: string) => void,
}

const DataTable = <T extends Object = {}>({
  tableRef,
  title,
  isLoading,
  columns,
  data,
  onRowClick,
  pageSize,
  style,
  isScrollable,
  sorting = true,
  defaultOrderBy = -1,
  defaultOrderDirection = 'asc',
  defaultOrderByField = '',
  onOrderChange,
} : Props<T>) => {
  const pagination = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const rowStyle = useCallback(() => ({
    color: 'var(--gray-800)'
  }), []);

  const handleOrderChange = useCallback((orderBy: number, orderDirection: 'asc' | 'desc') => {
    if (onOrderChange) {
      const column = columns[orderBy];
      const orderByField = column?.field?.toString() ?? '';
      onOrderChange(orderBy, orderDirection, orderByField);
    }
  }, [onOrderChange, columns]);

  const [t] = useTranslation('shared');
  const [tPagination] = useTranslation('pagination', { keyPrefix: 'pagination' });

  const hasPaging = !!(pageSize && pageSize > 0);

  let tableStyle: React.CSSProperties = { marginBottom: 0 };

  if (isScrollable) {
    tableStyle = {...tableStyle, minWidth: '100%', width: 'fit-content'};
  }

  return (<>
    <div ref={containerRef} style={{...style, position: 'relative'}} className={`supplier-db-main-table-wrap ${styles.container} ${isScrollable ? styles.isScrollable : ''}`}>
      <MaterialTable<T>
        key={`${defaultOrderBy}-${defaultOrderDirection}-${defaultOrderByField}`}
        tableRef={tableRef}
        title={title}
        icons={tableIcons}
        style={tableStyle}
        isLoading={isLoading}
        options={{
          sorting: sorting,
          draggable: false,
          search: false,
          toolbar: false,
          paging: hasPaging,
          pageSize: pageSize,
          detailPanelColumnStyle: { display: 'none' },
          rowStyle: rowStyle,
          columnResizable: false,
          defaultOrderByCollection: defaultOrderBy >= 0 ? [{
            orderBy: defaultOrderBy,
            orderDirection: defaultOrderDirection,
            sortOrder: 0,
            orderByField: defaultOrderByField || columns[defaultOrderBy]?.field?.toString() || ''
          }] : []
        }}
        localization={{ body: { emptyDataSourceMessage: t('dataTable.emptyMessage', 'Kunde inte hitta något resultat') } }}
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        onOrderChange={handleOrderChange}
        components={{
          Pagination: ({page, count, onPageChange}) => {
            if (!hasPaging) return null;
            return pagination.current && createPortal(
              <PaginationWithGoTo
                count={Math.ceil(count/10)}
                onChange={(_, value) => onPageChange(_, value - 1)}
                page={page + 1}
                t={tPagination}
              />, pagination.current
            );
          },
          OverlayLoading: (props) => {
            return (
              <div style={{
                display: "table",
                width: "100%",
                height: "100%",
                backgroundColor: alpha(props.theme.palette.background.paper, 0.7)
              }}>
                <div style={{
                  display: "table-cell",
                  width: "100%",
                  height: "100%",
                  verticalAlign: "middle",
                  textAlign: "center"
                }}>
                  <CircularProgress />
                </div>
              </div>
            );
          }
        }}
      />
    </div>
    {hasPaging && <div ref={pagination} className="sup-pagination org-pagination" style={{display: 'flex', justifyContent: 'center', marginTop: '16px'}}></div>}
    </>
  );
}

export default DataTable;
